import React, {ChangeEvent, CSSProperties, ReactNode, useMemo} from 'react';
import {defineFormField, FormFieldProps} from "@web-libs/core/forms/ui/fields/shared/form-field.types";
import {FieldError} from "@web-libs/core/forms/ui/fields/shared/field-error";
import {Text} from "@web-libs/ui/ui-components/typo/text";

import css from './checkbox.module.css'
import Checkmark from "@web-libs/ui/svgs/icons/Checkmark";
import {Color, getCssVar} from "@web-libs/features/colors/application/colors";
import Select from "react-select";

export type CheckboxProps = FormFieldProps<boolean> & {
    color?: Color
    style?: React.CSSProperties
    children?: ReactNode,
}

export const Checkbox = defineFormField<CheckboxProps, HTMLInputElement, boolean>(function (props, ref) {
    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        props.onChange?.(event)
    }

    const currentColor = useMemo<Color>(() => {
        const _color: Color = props.color ?? 'dark'
        return props.error ? "warn" : _color
    }, [props.error, props.color])

    return <div>
        <div style={{display: "flex", alignItems: "center", ...props.style}}>
            <div className={css.container}>
                <input
                    ref={ref}
                    type="checkbox"
                    name={props.name}
                    checked={props.value}
                    onChange={onChange}
                    onBlur={props.onBlur}
                    style={{
                        height: 20,
                        width: 20,
                        marginRight: 8
                    }}
                />
                <span style={{borderColor: getCssVar(currentColor)}} className={css.checkmark}>
                <Checkmark color={currentColor} className={css.checkmarkIndicator}></Checkmark>
            </span>
            </div>
            <label>
                {props.label && <Text color={currentColor} size={"sm"}>{props.label}</Text>}
                {props.children}
            </label>
        </div>
        <FieldError error={props.error}/>
    </div>

})

export type DropdownProps = FormFieldProps<string> & {
    style?: CSSProperties
    color?: Color,
    placeholder?: string,
    noSelectionLabel?: string,
    options: { label: string, value: string }[]
}

export const Dropdown = defineFormField<DropdownProps, any, string>(
    function (props, ref) {
        const onChange = (value: any) => {
            const event:ChangeEvent<any> = {
                target: {
                    name: props.name,
                    value: value,
                }
            } as ChangeEvent<any>
            props.onChange?.(event)
        }

        const currentColor = useMemo<Color>(() => {
            const _color: Color = props.color ?? 'dark'
            return props.error ? "warn" : _color
        }, [props.error, props.color])

        const contraryColor = useMemo<Color>(() => {
            if(props.error) return "dark";
            if(props.color === "dark") return "light";
            if(props.color === "light") return "dark";
            if(props.color === "primary") return "onPrimary";
            return "dark";
        }, [props.error, props.color])

        const selectStyles = {
            control: (styles: any) => ({ ...styles }),
            option: (styles: any, {isSelected}: any) => ({ ...styles, ...{color:getCssVar(contraryColor)}}),
            menu: (styles: any) => ({ ...styles }),
        }

        const _options = [
            ...(props.noSelectionLabel ? [{label: props.noSelectionLabel, value: null}] : []),
            ...props.options
        ]

        return (
            <div style={props.style}>
                <div style={{marginBottom: 8}}>
                <Text  color={currentColor} size={"sm"}>{props.label}</Text>
                </div>
                <Select
                    ref={ref}
                    placeholder={props.placeholder ?? ""}
                    styles={selectStyles}
                    options={_options}
                    onChange={(option: any) => {
                        onChange(option.value)
                    }}
                    isSearchable={false}
                />
                <FieldError error={props.error}/>
            </div>
        )
    })
